// Scope filters with a live count (Lane 1322). Used to start a new event and
// to add parts to an existing one. Nothing is saved until the buyer confirms;
// the count comes from the server's own preview.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { formatMoney, formatWindow } from './sourcingApi';

export function SupplierPicker({ api, value, onChange, placeholder = 'Search your suppliers' }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    let live = true;
    const t = setTimeout(() => {
      api.searchSuppliers(q).then((r) => { if (live) setResults(r.results || []); })
        .catch(() => { if (live) setResults([]); });
    }, 200);
    return () => { live = false; clearTimeout(t); };
  }, [q, open, api]);

  useEffect(() => {
    const close = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', close);
    return () => document.removeEventListener('pointerdown', close);
  }, []);

  if (value) {
    return (
      <span className="srce-chip">
        {value.supplier_name}
        <button type="button" className="srce-chip-x" aria-label={`Remove ${value.supplier_name}`}
          onClick={() => onChange(null)}>
          <svg viewBox="0 0 12 12" width="10" height="10"><path d="M2 2l8 8M10 2l-8 8" stroke="currentColor" strokeWidth="1.6" /></svg>
        </button>
      </span>
    );
  }
  return (
    <div className="srce-pick" ref={boxRef}>
      <input className="srce-input" value={q} placeholder={placeholder} aria-label={placeholder}
        onFocus={() => setOpen(true)} onChange={(e) => { setQ(e.target.value); setOpen(true); }} />
      {open && results.length > 0 && (
        <ul className="srce-pick-list" role="listbox">
          {results.map((s) => (
            <li key={s.supplier_id}>
              <button type="button" className="srce-pick-item" onClick={() => { onChange(s); setOpen(false); setQ(''); }}>
                <span>{s.supplier_name}</span>
                <span className="srce-mut">{s.l12m_spend > 0 ? `${formatMoney(s.l12m_spend)} last 12 months` : 'no spend last 12 months'}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function emptyFilters() {
  return { level_1: '', level_2: '', sites: [], supplier: null };
}

export function toServerFilters(f) {
  const out = { sites: f.sites };
  if (f.level_1) out.level_1 = f.level_1;
  if (f.level_2) out.level_2 = f.level_2;
  if (f.supplier) out.bought_from_supplier_id = f.supplier.supplier_id;
  return out;
}

function hasAny(f) {
  return Boolean(f.level_1 || f.level_2 || f.sites.length || f.supplier);
}

export default function ScopeBuilder({ api, options, filters, onChange, preview }) {
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const categories = useMemo(() => (options && options.categories) || [], [options]);
  const sites = (options && options.sites) || [];
  const level2 = useMemo(() => {
    const c = categories.find((x) => x.level_1 === filters.level_1);
    return c ? c.level_2 : [];
  }, [categories, filters.level_1]);

  useEffect(() => {
    if (!hasAny(filters)) { setResult(null); setError(''); return undefined; }
    let live = true;
    setBusy(true);
    const t = setTimeout(() => {
      preview(toServerFilters(filters))
        .then((r) => { if (live) { setResult(r); setError(''); } })
        .catch((e) => { if (live) { setResult(null); setError(e.message); } })
        .finally(() => { if (live) setBusy(false); });
    }, 300);
    return () => { live = false; clearTimeout(t); };
  }, [filters, preview]);

  const toggleSite = (site) => onChange({
    ...filters,
    sites: filters.sites.includes(site) ? filters.sites.filter((s) => s !== site) : [...filters.sites, site],
  });

  return (
    <div className="srce-builder">
      <div className="srce-field">
        <label className="srce-label" htmlFor="srce-cat">Category</label>
        <div className="srce-row">
          <select id="srce-cat" className="ll-sel" value={filters.level_1}
            onChange={(e) => onChange({ ...filters, level_1: e.target.value, level_2: '' })}>
            <option value="">Any category</option>
            {categories.map((c) => <option key={c.level_1} value={c.level_1}>{c.label || c.level_1}</option>)}
          </select>
          <select className="ll-sel" value={filters.level_2} disabled={!filters.level_1}
            aria-label="Subcategory"
            onChange={(e) => onChange({ ...filters, level_2: e.target.value })}>
            <option value="">{filters.level_1 ? 'Any subcategory' : 'Pick a category first'}</option>
            {level2.map((c) => <option key={c.level_2} value={c.level_2}>{c.label || c.level_2}</option>)}
          </select>
        </div>
      </div>
      <div className="srce-field">
        <span className="srce-label">Sites</span>
        <div className="srce-sites">
          {sites.length === 0 && <span className="srce-mut">No sites in your purchase history</span>}
          {sites.map((s) => (
            <label key={s} className={`srce-site${filters.sites.includes(s) ? ' srce-site--on' : ''}`}>
              <input type="checkbox" checked={filters.sites.includes(s)} onChange={() => toggleSite(s)} />
              {s}
            </label>
          ))}
        </div>
      </div>
      <div className="srce-field">
        <span className="srce-label">Bought from</span>
        <SupplierPicker api={api} value={filters.supplier}
          onChange={(supplier) => onChange({ ...filters, supplier })} />
      </div>
      <div className="srce-preview" aria-live="polite">
        {!hasAny(filters) && <span className="srce-mut">Choose at least one filter.</span>}
        {hasAny(filters) && busy && <span className="srce-mut">Counting…</span>}
        {hasAny(filters) && !busy && error && <span className="srce-crit">{error}</span>}
        {hasAny(filters) && !busy && result && (
          <span>
            <b>{result.lines.toLocaleString()}</b> {result.lines === 1 ? 'line' : 'lines'} ({result.parts.toLocaleString()} parts
            {' '}at {result.sites.length} {result.sites.length === 1 ? 'site' : 'sites'}), {formatMoney(result.spend)} spent
            {' '}{formatWindow(result.window)}
            {result.too_many && <span className="srce-crit"> More than 5,000 lines: narrow the filters.</span>}
          </span>
        )}
      </div>
    </div>
  );
}
